/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { Menu as AriaMenu } from 'react-aria-components/Menu';
import { Popover } from 'react-aria-components/Popover';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { useContextProps } from 'react-aria-components/slots';
import { MenuContext } from './context';
import styles from './styles.module.css';
import type { MenuProps } from './types';

/**
 * Menu - A dropdown menu component with keyboard navigation and selection
 *
 * Provides accessible dropdown menu functionality with support for single and
 * multiple selection modes. Includes sections, separators, and submenu support
 * with comprehensive keyboard navigation and ARIA compliance.
 *
 * IMPORTANT: Menu includes its own internal Popover. Do NOT wrap Menu in a
 * Popover component. Use the `popoverProps` prop to configure placement and
 * other popover settings.
 *
 * @example
 * ```tsx
 * // Basic menu with trigger
 * <MenuTrigger>
 *   <Button>Open Menu</Button>
 *   <Menu>
 *     <MenuItem>Edit</MenuItem>
 *     <MenuItem>Copy</MenuItem>
 *     <MenuItem>Delete</MenuItem>
 *   </Menu>
 * </MenuTrigger>
 * ```

 * @example
 * ```tsx
 * // Menu with custom placement
 * <MenuTrigger>
 *   <Button>Open Menu</Button>
 *   <Menu popoverProps={{ placement: 'top end' }}>
 *     <MenuItem>Edit</MenuItem>
 *     <MenuItem>Copy</MenuItem>
 *   </Menu>
 * </MenuTrigger>
 * ```
 *
 * @example
 * ```tsx
 * // Menu with action handler
 * <MenuTrigger>
 *   <Button>Actions</Button>
 *   <Menu onAction={(key) => console.log(key)}>
 *     <MenuItem id="edit">Edit</MenuItem>
 *     <MenuItem id="delete">Delete</MenuItem>
 *   </Menu>
 * </MenuTrigger>
 * ```
 *
 * @example
 * ```tsx
 * // Menu with sections and separators
 * <MenuTrigger>
 *   <Button>Open</Button>
 *   <Menu>
 *     <MenuSection>
 *       <MenuItem>New File</MenuItem>
 *       <MenuItem>Open File</MenuItem>
 *     </MenuSection>
 *
 *     <MenuSeparator />
 *
 *     <MenuItem>Settings</MenuItem>
 *   </Menu>
 * </MenuTrigger>
 * ```
 *
 * @example
 * ```tsx
 * // Menu with selection
 * <MenuTrigger>
 *   <Button>Group</Button>
 *   <Menu selectionMode='multiple'>
 *     <MenuItem>Option 1</MenuItem>
 *     <MenuItem>Option 2</MenuItem>
 *   </Menu>
 * </MenuTrigger>
 * ```
 *
 * @example
 * ```tsx
 * // Menu with submenu
 * <MenuTrigger>
 *   <Button>Actions</Button>
 *   <Menu>
 *     <MenuItem>New File</MenuItem>
 *     <MenuSubmenu>
 *       <MenuItem>Export</MenuItem>
 *       <Menu>
 *         <MenuItem>Export as PDF</MenuItem>
 *         <MenuItem>Export as CSV</MenuItem>
 *         <MenuItem>Export as JSON</MenuItem>
 *       </Menu>
 *     </MenuSubmenu>
 *     <MenuItem>Delete</MenuItem>
 *   </Menu>
 * </MenuTrigger>
 * ```
 *
 * @param props - {@link MenuProps}
 * @param props.ref - Forwarded ref to the underlying Menu element.
 * @param props.children - Menu items and sections to render.
 * @param props.classNames - CSS class names for menu elements.
 * @param props.popoverProps - Props passed to the internal Popover component.
 * @param props.selectionMode - Selection mode for menu items.
 * @param props.variant - Visual density variant for the menu.
 * @returns The rendered Menu component.
 */
export function Menu<T extends object>({ ref, ...props }: MenuProps<T>) {
  [props, ref] = useContextProps(props, ref ?? null, MenuContext);

  const {
    children,
    classNames,
    popoverProps,
    selectionMode = 'single',
    variant = 'cozy',
    ...rest
  } = props;

  return (
    <Popover
      {...popoverProps}
      className={composeRenderProps(classNames?.popover, (className) =>
        clsx(styles.popover, className),
      )}
    >
      <MenuContext.Provider value={{ variant }}>
        <AriaMenu
          {...rest}
          ref={ref}
          className={composeRenderProps(classNames?.menu, (className) =>
            clsx('group/menu', styles.menu, styles[variant], className),
          )}
          selectionMode={selectionMode}
        >
          {children}
        </AriaMenu>
      </MenuContext.Provider>
    </Popover>
  );
}
